import React, {
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
  useRef
} from 'react';
import CompanyActionThemeScope from 'src/theme/CompanyActionThemeScope';
import { CompanyAppearanceOwnerContext, useCompanyThemeSelection } from 'src/theme/useCompanyActionConfiguration';
import AppearanceBoundary from 'src/theme/AppearanceBoundary';
import { readStoredWorkspaceIdentity } from 'src/workspaces/AdminWorkspaceProvider';
import { clientDocumentCompany } from './clientDocumentBranding';
import './documentTheme.css';

export type DocumentTheme = {
  schemaVersion: number;
  header: string;
  headerText: string;
  body: string;
  bodyText: string;
  stripe: string;
  grid: string;
  subtotal: string;
  subtotalText: string;
  total: string;
  totalText: string;
  warning: string;
  warningText: string;
  line: number;
  density: string;
  striped: boolean;
  documentHeader: {
    showLogo: boolean;
    logoData: string;
    logoSize: string;
    layout: string;
    showCompany: boolean;
    showEvent: boolean;
    divider: boolean;
    background: string;
    text: string;
    accent: string;
  };
};
const colors = [
  'header',
  'headerText',
  'body',
  'bodyText',
  'stripe',
  'grid',
  'subtotal',
  'subtotalText',
  'total',
  'totalText',
  'warning',
  'warningText'
];
export const documentThemeConfiguration = (
  selection: any
): DocumentTheme | null => {
  const theme = selection?.configuration;
  if (
    theme?.schemaVersion !== 1 ||
    !colors.every((key) => /^#[0-9a-f]{6}$/i.test(theme[key])) ||
    ![1, 2, 3].includes(theme.line) ||
    !theme.documentHeader ||
    !['background', 'text', 'accent'].every((key) =>
      /^#[0-9a-f]{6}$/i.test(theme.documentHeader[key])
    )
  )
    return null;
  return theme;
};
// Presence distinguishes an unresolved payload from a resolved, explicitly unconfigured
// document. Keep the existing validator/fallback for legacy or unsupported configurations.
export function documentAppearanceStatus(company: any): 'pending' | 'ready' {
  return company && Object.prototype.hasOwnProperty.call(company, 'documentTheme') &&
    company.documentTheme !== undefined ? 'ready' : 'pending';
}
export const DocumentThemeContext = createContext<DocumentTheme | null>(null);
export const useDocumentTheme = () => useContext(DocumentThemeContext);
export function documentThemeStyle(
  theme: DocumentTheme | null
): React.CSSProperties {
  if (!theme) return {};
  const values: Record<string, string | number> = {};
  colors.forEach((key) => {
    values['--document-' + key] = theme[key];
  });
  values['--document-line'] = theme.line + 'px';
  values['--document-padding'] =
    theme.density === 'compact' ? '5px 6px' : '9px 8px';
  values['--document-alternate'] = theme.striped ? theme.stripe : theme.body;
  ['background', 'text', 'accent'].forEach((key) => {
    values['--document-brand-' + key] = theme.documentHeader[key];
  });
  return values as React.CSSProperties;
}

// Legacy document families render their totals independently. Classify existing rows,
// never reconstruct values or change the invoice/quote calculation state.
export function markDocumentThemeRows(root: HTMLElement) {
  root.querySelectorAll<HTMLTableRowElement>('tr').forEach((row) => {
    if (row.closest('[data-document-theme-exclude]')) return;
    const cells = Array.from(row.cells);
    const label =
      row.querySelector('[data-client-summary-label]')?.textContent?.trim() ||
      (cells.map((cell) => cell.textContent?.trim() ?? '').find(Boolean) ?? '');
    const subtotal = /^(?:(?:crew|staffing) sub\s?total|sub\s?total)\b/i.test(label);
    const total = !subtotal && (
      /^(?:grand total|total due|balance due|amount due|total amount|final total|admin fee|administrative fee|service fee|discount|deposit|paid deposit|paid amount|already paid|total paid|payments received|remaining|instant pay(?:ment)? fee|processing fee|payment method|tax|sales tax|surcharge|additional charges?|other charges?)\b/i.test(
        label
      ) || row.matches('[data-quote-summary-row], .client-pdf-summary-row') || Boolean(row.closest('.client-pdf-summary'))
    );
    if (total || subtotal)
      row.dataset.documentTotal = total ? 'grand' : 'subtotal';
    else delete row.dataset.documentTotal;
  });
}

type Props = React.HTMLAttributes<HTMLDivElement> & {
  document?: any;
  company?: any;
  refreshSelection?: boolean;
  deferThemeContent?: boolean;
  expectedCompanyId?: number;
  companyLoadError?: boolean;
  onRetryCompany?: () => void;
};
type SharedSelection = ReturnType<typeof useCompanyThemeSelection>;
const RealmDocumentThemeScope = forwardRef<HTMLDivElement, Props>(function RealmDocumentThemeScope(props, ref) {
  const identity = readStoredWorkspaceIdentity();
  const owner = useContext(CompanyAppearanceOwnerContext);
  const principal = identity.kind === 'realm' ? identity.principal : null;
  const samePrincipal = owner && owner.principalId === principal?.id && owner.role === principal?.role;
  const requiresOwner = Object.prototype.hasOwnProperty.call(props, 'document') || props.company !== undefined || Boolean(props.expectedCompanyId);
  // The verified company workspace can supply appearance before document/profile data.
  // Platform documents must explicitly identify the same owner; public documents bypass this scope.
  const canUseWorkspaceOwner = requiresOwner && !props.companyLoadError && samePrincipal &&
    (principal.role === 'COMPANY_ADMIN' || props.expectedCompanyId === owner.companyId) &&
    (!props.expectedCompanyId || props.expectedCompanyId === owner.companyId);
  const company = props.company ?? clientDocumentCompany(props.document) ?? (canUseWorkspaceOwner ? { id: owner.companyId } : props.company);
  const companyId = props.expectedCompanyId && Number(company?.id) !== props.expectedCompanyId ? null : company?.id;
  const selection = useCompanyThemeSelection(principal, companyId);
  const selectionErrorHandled = Boolean(samePrincipal && Number(companyId) === owner.companyId);
  return <DocumentThemeScopeContent {...props} company={company}
    sharedSelection={selection} selectionErrorHandled={selectionErrorHandled}
    ref={ref} />;
});
export const DocumentThemeScope = forwardRef<HTMLDivElement, Props>(function DocumentThemeScope(props, ref) {
  // Public/issued documents never consult the current Admin selection, even when signed in.
  return props.refreshSelection && readStoredWorkspaceIdentity().kind === 'realm'
    ? <RealmDocumentThemeScope {...props} ref={ref} />
    : <DocumentThemeScopeContent {...props} ref={ref} />;
});
const DocumentThemeScopeContent = forwardRef<HTMLDivElement, Props & { sharedSelection?: SharedSelection; selectionErrorHandled?: boolean }>(
  function DocumentThemeScope(
    scopeProps,
    forwardedRef
  ) {
    const {
      document,
      company: explicitCompany,
      refreshSelection,
      sharedSelection,
      selectionErrorHandled = false,
      deferThemeContent = true,
      expectedCompanyId,
      companyLoadError = false,
      onRetryCompany,
      children,
      style,
      ...props
    } = scopeProps;
    const documentCompany = explicitCompany ?? clientDocumentCompany(document);
    const company =
      expectedCompanyId && Number(documentCompany?.id) !== expectedCompanyId
        ? null
        : documentCompany;
    const inherited = useDocumentTheme();
    const root = useRef<HTMLDivElement>(null);
    const realmSelection = Boolean(sharedSelection);
    const requiresOwner = Object.prototype.hasOwnProperty.call(scopeProps, 'document') ||
      explicitCompany !== undefined || Boolean(expectedCompanyId);
    const embeddedStatus = documentAppearanceStatus(company);
    const resolved = realmSelection ? sharedSelection.data !== undefined : embeddedStatus === 'ready';
    const selectionPending = deferThemeContent && requiresOwner && (!company || !resolved);
    const selectionError = deferThemeContent && requiresOwner && Boolean(sharedSelection?.isError);
    const selected = realmSelection ? sharedSelection.data : company?.documentTheme;
    const theme = company ? documentThemeConfiguration(selected) : inherited;
    const appearanceBlocked = deferThemeContent && Boolean(selectionPending || (companyLoadError && !company));
    useLayoutEffect(() => {
      if (!root.current || !theme) return;
      const node = root.current;
      const update = () => markDocumentThemeRows(node);
      update();
      const observer = new MutationObserver(update);
      observer.observe(node, {
        childList: true,
        subtree: true,
        characterData: true
      });
      return () => observer.disconnect();
    }, [theme]);
    return (
      <DocumentThemeContext.Provider value={theme}>
        <CompanyActionThemeScope configuration={requiresOwner ? selected?.configuration ?? null : undefined} pending={appearanceBlocked}>
        <AppearanceBoundary
          {...props}
          pending={Boolean(selectionPending)}
          error={deferThemeContent && companyLoadError && !company
            ? 'Company profile could not be loaded. Retry to display the banner and table.'
            : selectionError && !selectionErrorHandled ? 'Current company appearance could not be loaded. Retry to display the table.' : undefined}
          retainContentOnError={resolved}
          onRetry={companyLoadError && !company ? onRetryCompany : () => { void sharedSelection?.refetch(); }}
          retryLabel={companyLoadError && !company ? 'Retry company profile' : 'Retry theme'}
          ref={(node) => {
            (root as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          data-document-theme={theme ? 'selected' : undefined}
          data-document-theme-pending={appearanceBlocked ? 'true' : undefined}
          data-document-layout={theme ? 'split' : undefined}
          data-document-company={
            theme ? String(theme.documentHeader.showCompany) : undefined
          }
          data-document-event={
            theme ? String(theme.documentHeader.showEvent) : undefined
          }
          data-document-divider={
            theme ? String(theme.documentHeader.divider) : undefined
          }
          style={{ ...documentThemeStyle(theme), ...style }}
        >
          {children}
        </AppearanceBoundary>
        </CompanyActionThemeScope>
      </DocumentThemeContext.Provider>
    );
  }
);
